// Eligibility quiz configuration and indicative logic.
// Grounded in docs/research (eligibility-quiz-and-leads.md) and
// australian-visa-pathways.md. COMPLIANCE (Code s 26, D-003): indicative
// guidance only, never an approval prediction or guarantee. The points
// ruleset is VERSIONED config (figures reindex on 1 July and the 2026
// structure is under review), never a promise of a score.

export const QUIZ_RULESET_VERSION = "pts-2026-06";

export const NOT_ADVICE =
  "This quiz is general information, not personal migration advice. A consultation gives you advice for your situation.";

export const RESULT_DISCLAIMER =
  "This quiz gives general, indicative guidance only. It is not personal migration advice and not a prediction of any outcome. Visa decisions are made solely by the Department of Home Affairs, and no outcome is guaranteed.";

export const OFFICIAL_CALCULATOR =
  "https://immi.homeaffairs.gov.au/help-support/tools/points-calculator";

// Step 1 triage.
export const GOALS = [
  { key: "skilled", label: "Skilled migration", sub: "Points-tested permanent residence" },
  { key: "study", label: "Study in Australia", sub: "Student visa" },
  { key: "partner-family", label: "Partner or family", sub: "Join family in Australia" },
  { key: "employer-sponsored", label: "Employer sponsored", sub: "A job-based visa" },
  { key: "visitor-working-holiday", label: "Visitor or working holiday", sub: "Short stays" },
  { key: "refusal-appeal", label: "Visa refusal or appeal", sub: "Time-critical" },
  { key: "not-sure", label: "Not sure yet", sub: "Help me work it out" },
];

// Step 2 per-path question sets. Each question:
//   { key, question, note?, type:'choice'|'text'|'date', optional?, options:[{value,label,points?}] }
// `points` is only used on the skilled path for the indicative band.
export const PATHS = {
  skilled: [
    {
      key: "age",
      question: "What is your age?",
      type: "choice",
      options: [
        { value: "18-24", label: "18 to 24", points: 25 },
        { value: "25-32", label: "25 to 32", points: 30 },
        { value: "33-39", label: "33 to 39", points: 25 },
        { value: "40-44", label: "40 to 44", points: 15 },
        { value: "45+", label: "45 or over", points: 0 },
      ],
    },
    {
      key: "occupation_listed",
      question: "Is your occupation on a current skilled occupation list?",
      note: "The occupation list is decisive. We confirm it with you in a consultation.",
      type: "choice",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
        { value: "unsure", label: "Not sure" },
      ],
    },
    {
      key: "english",
      question: "What is your English level?",
      type: "choice",
      options: [
        { value: "superior", label: "Superior (about IELTS 8)", points: 20 },
        { value: "proficient", label: "Proficient (about IELTS 7)", points: 10 },
        { value: "competent", label: "Competent (about IELTS 6)", points: 0 },
        { value: "below", label: "Below competent, or not tested yet", points: 0 },
      ],
    },
    {
      key: "qualification",
      question: "What is your highest qualification?",
      type: "choice",
      options: [
        { value: "doctorate", label: "Doctorate", points: 20 },
        { value: "bachelor-masters", label: "Bachelor or Masters", points: 15 },
        { value: "diploma-trade", label: "Diploma or trade", points: 10 },
        { value: "none", label: "None of these", points: 0 },
      ],
    },
    {
      key: "experience",
      question: "How much skilled work experience do you have in your occupation?",
      type: "choice",
      options: [
        { value: "8+", label: "8 years or more", points: 15 },
        { value: "5-7", label: "5 to 7 years", points: 10 },
        { value: "3-4", label: "3 to 4 years", points: 5 },
        { value: "1-2", label: "1 to 2 years", points: 0 },
        { value: "under-1", label: "Under 1 year", points: 0 },
      ],
    },
    {
      key: "skills_assessment",
      question: "Do you have a positive skills assessment?",
      type: "choice",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
        { value: "unsure", label: "Not sure" },
      ],
    },
    {
      key: "nomination",
      question: "Are you open to state or regional nomination?",
      note: "Nomination adds points: a state nomination (190) or a regional nomination (491).",
      type: "choice",
      options: [
        { value: "state", label: "Yes, a state (190)", points: 5 },
        { value: "regional", label: "Yes, regional (491)", points: 15 },
        { value: "either", label: "Either is fine", points: 15 },
        { value: "no", label: "No", points: 0 },
      ],
    },
    {
      key: "partner",
      question: "Is a partner included in your application?",
      type: "choice",
      optional: true,
      options: [
        { value: "single-or-citizen", label: "Single, or partner is a citizen/PR", points: 10 },
        { value: "partner-skilled", label: "Partner with skills", points: 10 },
        { value: "partner-unskilled", label: "Partner without a skills assessment", points: 0 },
      ],
    },
  ],

  study: [
    {
      key: "study_level",
      question: "What do you want to study?",
      type: "choice",
      options: [
        { value: "vet", label: "Vocational or trade" },
        { value: "bachelor", label: "Bachelor degree" },
        { value: "postgrad", label: "Postgraduate" },
        { value: "english", label: "English (ELICOS)" },
        { value: "undecided", label: "Not decided" },
      ],
    },
    {
      key: "highest_qualification",
      question: "What is your highest qualification so far?",
      type: "choice",
      options: [
        { value: "postgrad", label: "Postgraduate" },
        { value: "bachelor", label: "Bachelor" },
        { value: "diploma", label: "Diploma or trade" },
        { value: "secondary", label: "Secondary school" },
      ],
    },
    {
      key: "english",
      question: "What is your English level?",
      type: "choice",
      options: [
        { value: "strong", label: "Strong" },
        { value: "moderate", label: "Moderate" },
        { value: "not-tested", label: "Not tested yet" },
      ],
    },
    {
      key: "funds",
      question:
        "Can you show funds for tuition plus living costs (a living-cost guide of about AUD 29,710 for a single applicant, confirm current)?",
      type: "choice",
      options: [
        { value: "yes", label: "Yes" },
        { value: "partly", label: "Partly" },
        { value: "unsure", label: "Not sure" },
      ],
    },
    {
      key: "location",
      question: "Are you currently in Australia or offshore?",
      type: "choice",
      options: [
        { value: "onshore", label: "In Australia" },
        { value: "offshore", label: "Offshore" },
      ],
    },
    {
      key: "to_skilled",
      question: "Interested in a study-to-skilled pathway later?",
      note: "Honest note: study is an expensive, indirect, and non-guaranteed route to PR.",
      type: "choice",
      optional: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "maybe", label: "Maybe" },
        { value: "no", label: "No" },
      ],
    },
  ],

  "partner-family": [
    {
      key: "relationship",
      question: "What is your relationship to the person in Australia?",
      type: "choice",
      options: [
        { value: "married", label: "Married" },
        { value: "defacto", label: "De facto (living together)" },
        { value: "engaged", label: "Engaged to marry" },
        { value: "parent", label: "Parent of an Australian" },
        { value: "child", label: "Child of an Australian" },
        { value: "other", label: "Other relative" },
      ],
    },
    {
      key: "sponsor_status",
      question:
        "Is your partner or sponsor an Australian citizen, permanent resident, or eligible NZ citizen?",
      type: "choice",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
        { value: "unsure", label: "Not sure" },
      ],
    },
    {
      key: "location",
      question: "Are you in Australia or offshore?",
      type: "choice",
      options: [
        { value: "onshore", label: "In Australia" },
        { value: "offshore", label: "Offshore" },
      ],
    },
    {
      key: "defacto_duration",
      question:
        "For a de facto relationship: have you lived together 12 months or more, or registered the relationship?",
      type: "choice",
      optional: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
        { value: "na", label: "Not applicable" },
      ],
    },
    {
      key: "balance_of_family",
      question:
        "For a parent visa: do at least half your children live in Australia (balance-of-family)?",
      type: "choice",
      optional: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
        { value: "unsure", label: "Not sure" },
        { value: "na", label: "Not applicable" },
      ],
    },
  ],

  "employer-sponsored": [
    {
      key: "has_sponsor",
      question: "Do you have an Australian employer willing to sponsor you?",
      note: "We assist eligible applicants and sponsors. We do not find jobs or promise sponsorship.",
      type: "choice",
      options: [
        { value: "yes", label: "Yes" },
        { value: "likely", label: "Likely" },
        { value: "no", label: "No, still looking" },
      ],
    },
    {
      key: "occupation_listed",
      question: "Is your occupation on a current list?",
      type: "choice",
      options: [
        { value: "yes", label: "On the list" },
        { value: "unsure", label: "Not sure" },
        { value: "no", label: "Not on the list" },
      ],
    },
    {
      key: "experience",
      question: "Years of relevant experience?",
      type: "choice",
      options: [
        { value: "3+", label: "3 years or more" },
        { value: "1-2", label: "1 to 2 years" },
        { value: "under-1", label: "Under 1 year" },
      ],
    },
    {
      key: "english",
      question: "What is your English level?",
      type: "choice",
      options: [
        { value: "strong", label: "Strong" },
        { value: "moderate", label: "Moderate" },
        { value: "not-tested", label: "Not tested yet" },
      ],
    },
    {
      key: "salary_band",
      question: "Indicative salary band for the role (confirm current thresholds)?",
      type: "choice",
      options: [
        { value: "specialist", label: "Above about AUD 146,717 (specialist)" },
        { value: "core", label: "About AUD 79,499 to 146,717" },
        { value: "below", label: "Below about AUD 79,499" },
      ],
    },
    {
      key: "region",
      question: "Where is the role located?",
      type: "choice",
      options: [
        { value: "metro", label: "A major city" },
        { value: "regional", label: "Regional Australia" },
      ],
    },
  ],

  "visitor-working-holiday": [
    {
      key: "purpose",
      question: "What is the purpose of your visit?",
      type: "choice",
      options: [
        { value: "tourism", label: "Tourism" },
        { value: "family", label: "Visit family" },
        { value: "business", label: "Business visit" },
        { value: "working-holiday", label: "Working holiday" },
      ],
    },
    {
      key: "passport_country",
      question: "What passport do you hold?",
      note: "This indicates working-holiday eligibility (417 vs 462) and age caps.",
      type: "text",
      placeholder: "e.g. United Kingdom",
    },
    {
      key: "age_band",
      question: "What is your age?",
      type: "choice",
      options: [
        { value: "18-30", label: "18 to 30" },
        { value: "31-35", label: "31 to 35" },
        { value: "36+", label: "36 or over" },
      ],
    },
    {
      key: "location",
      question: "Are you in Australia now or offshore?",
      type: "choice",
      options: [
        { value: "onshore", label: "In Australia" },
        { value: "offshore", label: "Offshore" },
      ],
    },
  ],

  "refusal-appeal": [
    {
      key: "decision_received",
      question: "Have you received a refusal or cancellation decision?",
      type: "choice",
      options: [
        { value: "yes", label: "Yes" },
        { value: "pending", label: "Pending" },
        { value: "no", label: "No" },
      ],
    },
    {
      key: "decision_date",
      question: "What is the date on the decision letter?",
      note: "Review time limits are short and strict (commonly 21 or 28 days). The date matters.",
      type: "date",
    },
    {
      key: "visa_refused",
      question: "Which visa was refused or cancelled?",
      type: "text",
      placeholder: "e.g. Student visa (500)",
    },
    {
      key: "location",
      question: "Are you in Australia or offshore?",
      type: "choice",
      optional: true,
      options: [
        { value: "onshore", label: "In Australia" },
        { value: "offshore", label: "Offshore" },
        { value: "detention", label: "In Australia, in detention" },
      ],
    },
  ],

  "not-sure": [
    {
      key: "location",
      question: "Are you in Australia or offshore?",
      type: "choice",
      options: [
        { value: "onshore", label: "In Australia" },
        { value: "offshore", label: "Offshore" },
      ],
    },
    {
      key: "rough_goal",
      question: "Roughly, what are you hoping for?",
      type: "choice",
      options: [
        { value: "stay-permanently", label: "To live in Australia permanently" },
        { value: "work", label: "To work in Australia" },
        { value: "study", label: "To study" },
        { value: "visit", label: "To visit" },
      ],
    },
    {
      key: "timeframe",
      question: "What is your timeframe?",
      type: "choice",
      options: [
        { value: "asap", label: "As soon as possible" },
        { value: "1-2y", label: "Within 1 to 2 years" },
        { value: "flexible", label: "Flexible" },
      ],
    },
  ],
};

// Compute an indicative skilled points band from the answers (versioned).
function computeSkilledBand(answersByKey) {
  const opt = (qKey, vKey) =>
    (PATHS.skilled.find((q) => q.key === qKey)?.options || []).find(
      (o) => o.value === answersByKey[qKey]
    );
  const pts = (qKey) => opt(qKey, answersByKey[qKey])?.points || 0;

  const base =
    pts("age") + pts("english") + pts("qualification") + pts("experience") + pts("partner");
  const nominationBonus = pts("nomination");
  return {
    min: base,
    max: base + nominationBonus,
    rulesetVersion: QUIZ_RULESET_VERSION,
  };
}

// recommend(goal, answers[]) -> indicative result. answers: [{key,question,answer,value}].
export function recommend(goal, answers) {
  const byKey = {};
  answers.forEach((a) => {
    byKey[a.key] = a.value;
  });

  const baseNotes = [];

  if (goal === "skilled") {
    const band = computeSkilledBand(byKey);
    let pathways = [];
    switch (byKey.nomination) {
      case "state":
        pathways = ["skilled-nominated-190", "skilled-independent-189"];
        break;
      case "regional":
        pathways = ["skilled-regional-491", "skilled-nominated-190"];
        break;
      case "either":
        pathways = ["skilled-nominated-190", "skilled-regional-491", "skilled-independent-189"];
        break;
      default:
        pathways = ["skilled-independent-189", "skilled-nominated-190"];
    }
    if (byKey.skills_assessment !== "yes") pathways.push("skills-assessment");
    const notes = [
      "65 points is only the minimum to enter the pool. Actual invitation cut-offs are higher and depend on your occupation.",
      "A positive skills assessment and at least competent English are prerequisites for most points.",
      "Processing is set by the Department and is commonly measured in months.",
    ];
    if (byKey.occupation_listed !== "yes")
      notes.unshift("Your occupation being on the applicable list is decisive. We confirm this first.");
    return {
      headline: "Skilled migration may be a pathway for you",
      rationale:
        "Based on your answers, here is an indicative points band and the skilled visas worth exploring. This is a guide only, not a score.",
      pathways,
      pointsBand: band,
      notes,
      priority: "normal",
    };
  }

  if (goal === "study") {
    const pathways = ["student-500"];
    if (byKey.to_skilled === "yes" || byKey.to_skilled === "maybe")
      pathways.push("graduate-485", "admissions");
    else pathways.push("admissions");
    return {
      headline: "A student visa may be a pathway for you",
      rationale: "Here are the study options worth exploring, with an honest view of the requirements.",
      pathways,
      notes: [
        "The Genuine Student requirement applies, and you must show financial capacity.",
        "Study is a temporary visa. Any later move to skilled or graduate visas is an indirect, non-guaranteed path.",
      ],
      priority: "normal",
    };
  }

  if (goal === "partner-family") {
    let pathways = [];
    switch (byKey.relationship) {
      case "married":
      case "defacto":
        pathways = ["partner-visa"];
        break;
      case "engaged":
        pathways = ["prospective-marriage-300", "partner-visa"];
        break;
      case "parent":
        pathways = ["parent-visa"];
        break;
      case "child":
        pathways = ["child-visa"];
        break;
      default:
        pathways = ["partner-visa", "parent-visa", "child-visa"];
    }
    const notes = [
      byKey.location === "onshore"
        ? "Onshore partner applications use the 820 then 801 stages, with a bridging visa while you wait."
        : "Offshore partner applications use the 309 then 100 stages.",
      "Relationship evidence is the heart of a partner application.",
    ];
    if (byKey.relationship === "parent")
      notes.push(
        "Parent visas are subject to the balance-of-family test, and queues can be very long. We will be candid about cost and time."
      );
    return {
      headline: "A family or partner visa may be a pathway for you",
      rationale: "Here is the indicative family pathway and what it depends on.",
      pathways,
      notes,
      priority: "normal",
    };
  }

  if (goal === "employer-sponsored") {
    const pathways = ["skills-in-demand-482", "employer-nomination-186"];
    if (byKey.region === "regional") pathways.push("regional-494");
    return {
      headline: "An employer-sponsored visa may be a pathway for you",
      rationale: "Here are the sponsored options worth exploring.",
      pathways,
      notes: [
        "You need an approved sponsoring employer and an eligible occupation.",
        "We assist eligible applicants and sponsors. We do not find jobs or guarantee sponsorship, and the Department decides.",
      ],
      priority: "normal",
    };
  }

  if (goal === "visitor-working-holiday") {
    const pathways =
      byKey.purpose === "working-holiday" ? ["working-holiday"] : ["visitor-600"];
    if (byKey.purpose === "family") pathways.push("sponsored-family-visitor");
    return {
      headline: "A short-stay visa may suit you",
      rationale: "Here is the indicative short-stay pathway.",
      pathways,
      notes: ["These are temporary visas with no direct pathway to permanent residence."],
      priority: "normal",
    };
  }

  if (goal === "refusal-appeal") {
    return {
      headline: "Time limits to seek review are short and strict",
      rationale:
        "If you have a refusal or cancellation, do not wait. Book a consultation now so we can confirm your review rights and the deadline.",
      pathways: ["visa-refusals", "visa-cancellations"],
      notes: [
        "Review deadlines are commonly 21 or 28 days and missing them usually ends the right to review.",
        "No one can predict the result of a review. We confirm your options and act quickly.",
      ],
      priority: "high",
    };
  }

  // not-sure
  return {
    headline: "A consultation is the best next step",
    rationale:
      "From your answers, the clearest way forward is a short consultation to map your realistic options honestly.",
    pathways: [],
    notes: baseNotes,
    priority: "normal",
  };
}
